import { IconEye, IconEyeOff, IconCheck, IconX } from "@tabler/icons-react";
import React, { useState, useEffect } from "react";
import { UseFormRegister, FieldError, RegisterOptions } from "react-hook-form";
import { useLanguage } from "../context/LanguageContext";

interface InputProps {
  name: string;
  type?: string;
  title?: string;
  placeholder?: string;
  register?: UseFormRegister<any>;
  validation?: RegisterOptions;
  error?: FieldError | any;
  other?: any;
  className?: string;
  labelClass?: string;
  disabled?: boolean;
  defaultValue?: string;
  value?: string | number;
  removeDiv?: boolean;
  step?: any;
  inputMode?: any;
  inputClassName?: any;
  pattern?: string;
  min?: any;
  maxLength?: number;
  hideError?: boolean;
}

const Input: React.FC<InputProps> = ({
  name,
  title,
  type,
  placeholder = "",
  register,
  validation,
  error,
  other,
  className = "",
  labelClass = "",
  removeDiv = false,
  disabled = false,
  defaultValue = "",
  value,
  step,
  inputMode,
  inputClassName,
  pattern,
  min,
  maxLength,
  hideError = false,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [inputValue, setInputValue] = useState<string>(
    value !== undefined ? String(value) : defaultValue ? String(defaultValue) : ""
  );
  const { t } = useLanguage();

  useEffect(() => {
    if (value !== undefined) {
      setInputValue(String(value));
    }
  }, [value]);

  const registered = register ? register(name, validation) : ({} as any);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (type === "number" && maxLength && e.target.value.length > maxLength) {
      e.target.value = e.target.value.slice(0, maxLength);
    }
    setInputValue(e.target.value);
    if (registered.onChange) {
      registered.onChange(e);
    }
    if (other?.onChange) {
      other.onChange(e);
    }
  };

  const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    if (target.value !== inputValue) {
      setInputValue(target.value);
    }
    if (other?.onInput) {
      other.onInput(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (registered.onBlur) {
      registered.onBlur(e);
    }
    if (other?.onBlur) {
      other.onBlur(e);
    }
  };

  const isEmail = type === "email" || name?.toLowerCase().includes("email");
  const isPassword = type === "password" || name?.toLowerCase().includes("password");
  const isTargetInput = isEmail || isPassword;

  // Determine validation status icon (ONLY for email & password)
  const getValidationStatus = () => {
    if (!isTargetInput) {
      return null;
    }

    if (!inputValue || inputValue.trim() === "") {
      return null;
    }

    if (isEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isValidFormat = emailRegex.test(inputValue.trim());
      if (isValidFormat && !error) {
        return "valid";
      } else {
        return "invalid";
      }
    }

    if (isPassword) {
      if (error) {
        return "invalid";
      }
      const minLen = validation?.minLength ? Number(validation.minLength) : 6;
      if (inputValue.length < minLen) {
        return "invalid";
      }
      return "valid";
    }

    return null;
  };

  const validationStatus = getValidationStatus();
  const hasRightIcons = type === "password" || validationStatus !== null;

  // Add right padding so input text does not overlap icons
  const paddingRightClass =
    type === "password" && validationStatus !== null
      ? "!pr-16"
      : hasRightIcons
      ? "!pr-10"
      : "";

  let computedInputClassName = "";

  if (isTargetInput) {
    const isInvalid = validationStatus === "invalid" || Boolean(error);
    const isValid = validationStatus === "valid";
    const borderClass = isInvalid
      ? "border-red-500"
      : isValid
      ? "border-[#FBD037]"
      : "border-[#D8D8D8]/20";
    const focusClass = isInvalid
      ? "focus:border-red-500 focus:ring-1 focus:ring-red-500"
      : "focus:border-[#FBD037] focus:ring-1 focus:ring-[#FBD037]";

    const defaultInputClassName = `w-full rounded-full py-[6px] px-4 outline-none bg-yellow-50 border ${borderClass} text-[#1E1E1E] ${
      type === "number" ? "hide-spinner" : ""
    }`;

    let processedInputClassName = inputClassName || defaultInputClassName;
    if (inputClassName) {
      processedInputClassName = inputClassName
        .replace(/\bborder-\[[^\]]+\](\/\d+)?/g, "")
        .replace(/\bborder-gray-\d+/g, "")
        .replace(/\bborder-red-500/g, "")
        .trim();
      processedInputClassName += ` border ${borderClass}`;
    }

    computedInputClassName = `${processedInputClassName} ${paddingRightClass} outline-none ${focusClass}`;
  } else {
    const defaultInputClassName = `w-full rounded-full py-[6px] px-4 outline-none bg-yellow-50 border ${
      error ? "border-red-500" : " border-[#D8D8D8]/20"
    } text-[#1E1E1E] ${type === "number" ? "hide-spinner" : ""}`;

    computedInputClassName = inputClassName ? inputClassName : defaultInputClassName;
  }

  const inputField = (
    <input
      type={type === "password" && showPassword ? "text" : type}
      pattern={pattern}
      min={min}
      maxLength={maxLength}
      placeholder={t(placeholder)}
      className={computedInputClassName}
      {...(value !== undefined ? { value } : { defaultValue })}
      {...registered}
      {...other}
      onChange={handleChange}
      onInput={handleInput}
      onBlur={handleBlur}
      disabled={disabled}
      {...(step !== undefined ? { step } : {})}
      {...(inputMode !== undefined ? { inputMode } : {})}
      autoComplete="off"
      onKeyDown={(e) => {
        if (type === "number" && ["e", "E", "+", "-"].includes(e.key)) {
          e.preventDefault();
        }
        if (other?.onKeyDown) {
          other.onKeyDown(e);
        }
      }}
    />
  );

  return (
    <div className={`space-y-1 ${className}`}>
      {title && (
        <label
          className={labelClass ? labelClass : "text-[#1E293B] text-sm whitespace-nowrap"}
        >
          {t(title)}
          {validation?.required && <span className="text-red-600 ml-1">*</span>}
        </label>
      )}
      <div className="w-full">
        {!removeDiv ? (
          <div className="relative flex items-center">
            {inputField}
            {hasRightIcons && (
              <div className="absolute right-3.5 top-1/2 -translate-y-1/2 flex items-center gap-2 z-10 pointer-events-auto">
                {validationStatus === "valid" && (
                  <span
                    className="w-5 h-5 rounded-full bg-[#FBD037] text-black flex items-center justify-center shadow-sm transition-all duration-200"
                    title="Valid"
                  >
                    <IconCheck size={13} stroke={3} />
                  </span>
                )}
                {validationStatus === "invalid" && (
                  <span
                    className="w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center shadow-sm transition-all duration-200"
                    title="Invalid format"
                  >
                    <IconX size={13} stroke={3} />
                  </span>
                )}
                {type === "password" && (
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-gray-500 hover:text-gray-700 focus:outline-none flex items-center"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <IconEyeOff size={18} />
                    ) : (
                      <IconEye size={18} />
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          inputField
        )}
      </div>
      {!hideError &&
        error &&
        name !== "receiver.mobile" &&
        name !== "sender.mobile" &&
        (error?.message?.includes("\n") ? (
          error?.message?.split("\n").map((line: string, index: number) => (
            <span
              key={`val${index + 1}`}
              className="text-red-500 text-xs block font-light"
            >
              {t(line)}
            </span>
          ))
        ) : (
          <span className="text-red-500 text-xs block font-light">
            {error?.message ? t(error.message) : ""}
          </span>
        ))}
    </div>
  );
};

export default Input;